"use client";

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface EditShowedUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCount: number | null;
  maxCount: number;
  onSave: (count: number) => Promise<void>;
}

/**
 * EditShowedUpModal - Modal for editing the number of participants who showed uppppp
 *
 * Used in bar staff calendar to update attendance for a booking.
 *
 * @example
 * <EditShowedUpModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   currentCount={5}
 *   maxCount={10}
 *   onSave={handleSave}
 * />
 */
export function EditShowedUpModal({
  isOpen,
  onClose,
  currentCount,
  maxCount,
  onSave,
}: EditShowedUpModalProps) {
  const [count, setCount] = useState<number>(currentCount ?? 0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update local count when currentCount changes
  React.useEffect(() => {
    if (currentCount !== null) {
      setCount(currentCount);
    }
  }, [currentCount]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSave(count);
      onClose();
    } catch (error) {
      console.error("Error saving attendance:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow clearing the input without forcing a 0
    if (raw === "") {
      setCount(0);
      return;
    }
    const parsed = parseInt(raw.replace(/^0+(?!$)/, ""), 10);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      setCount(parsed);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Update attendance
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2 py-1">
          <div className="text-sm text-gray-600">
            How many participants actually showed up for this booking?
          </div>

          <div className="space-y-2">
            <Label htmlFor="attendance-count">Number of participants</Label>
            <Input
              id="attendance-count"
              type="number"
              min={0}
              value={count}
              onChange={handleInputChange}
              disabled={isSubmitting}
              className="w-full"
            />
            <div className="text-xs text-gray-500">
              Original booking: {maxCount} participants (you can record more or
              fewer)
            </div>
          </div>

          {count !== (currentCount ?? 0) && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <div className="font-medium text-blue-800">Attendance change</div>
              <div className="text-blue-700 text-xs mt-1">
                Updating from {currentCount ?? 0} to {count} participants
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || count < 0}>
            {isSubmitting ? "Updating..." : "Update attendance"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
